/**
 * The Bake Off League's browser side: the wire shapes, the reads and the
 * writes (api/src/functions/bakeOff.ts). The server decides what each
 * viewer may see; nothing here filters hidden data.
 */

import { useQuery } from '@tanstack/react-query';
import { apiErrorFrom } from './api';
import { writeFetch } from './writeFetch';

export interface BakeOffBaker {
  id: string;
  name: string;
}
export interface BakeOffWeek {
  number: number;
  theme: string;
  release_date: string;
}
export interface BakeOffSeason {
  name: string;
  bakers: BakeOffBaker[];
  weeks: BakeOffWeek[];
  draft_closed: boolean;
}
export interface BakeOffResults {
  star_baker: string | null;
  technical_winner: string | null;
  handshakes: string[];
  eliminated: string[];
}
export interface BakeOffGuess {
  star_baker: string;
  technical_winner: string;
  going_home: string;
}
export interface BakeOffPlayer {
  user_id: string;
  name: string;
  total: number;
  weeks: Record<string, number>;
  team?: { bakers: string[] };
  guesses?: Record<string, BakeOffGuess>;
}
export interface BakeOffView {
  season: BakeOffSeason | null;
  results: Record<string, BakeOffResults>;
  open_week: number | null;
  me: { team: { bakers: string[] } | null; guesses: Record<string, BakeOffGuess> };
  players: BakeOffPlayer[];
  is_owner: boolean;
}

export function bakeOffQueryKey(slug: string): [string, string] {
  return ['bake-off', slug];
}

function url(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1/bake-off${path}`;
}

export function useBakeOff(slug: string, opts: { enabled: boolean }) {
  return useQuery({
    queryKey: bakeOffQueryKey(slug),
    enabled: opts.enabled,
    queryFn: async ({ signal }): Promise<BakeOffView> => {
      const r = await fetch(url(slug, ''), { method: 'GET', headers: { Accept: 'application/json' }, signal });
      if (!r.ok) throw await apiErrorFrom(r, 'GET /bake-off');
      return (await r.json()) as BakeOffView;
    },
  });
}

/** One PUT; answers the fresh view. */
export async function putBakeOff(slug: string, path: string, body: Record<string, unknown>): Promise<BakeOffView> {
  const r = await writeFetch(
    url(slug, path),
    { method: 'PUT', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    { redirectToSignIn: true },
  );
  if (!r.ok) throw await apiErrorFrom(r, `PUT /bake-off${path}`);
  return (await r.json()) as BakeOffView;
}

/** A baker's name, or the id when the season no longer lists it. */
export function bakerName(season: BakeOffSeason, id: string | null | undefined): string {
  if (!id) return '';
  return season.bakers.find((b) => b.id === id)?.name ?? id;
}

/** The bakers still in for week N (not in an earlier week's eliminated list). */
export function bakersInFor(view: BakeOffView, week: number): BakeOffBaker[] {
  if (!view.season) return [];
  const gone = new Set<string>();
  for (const [w, r] of Object.entries(view.results)) if (Number(w) < week) for (const id of r.eliminated) gone.add(id);
  return view.season.bakers.filter((b) => !gone.has(b.id));
}
